import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    DialogContent,
    useTheme,
    capitalize,
} from "@mui/material";
import React, { useState } from "react";
import * as Yup from "yup";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import queryGraphql from "../../helpers/ApiInterface";
import { useAuth } from "../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";

import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import MiniBot from "../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";

// --- Interfaces ---
interface ApplicationInfo {
    applicationName: string;
}

interface SolutionOwnerInfo {
    name: string;
    googleProfilePicture: string;
}

interface AIAgentNestedDetails {
    projectId: string;
    region: string;
    imageUrl: string;
    gpu: string;
    modelName: string;
    modelTemperature: number;
    modelMaxTokens: number;
    modelStreaming: boolean;
    repoUrl?: string;
    network?: string;
    subnet?: string;
}

interface AIAgentDetailsData {
    id: string;
    name: string;
    environment: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    status: string;
    details: {
        aiAgent: AIAgentNestedDetails;
    };
    solutionOwner: SolutionOwnerInfo;
    solutionApprover?: SolutionOwnerInfo;
    application?: ApplicationInfo;
    reasonForRejection?: string;
}

interface ApprovalFormValues {
    network: string;
    subnet: string;
}

// --- Validation Schemas ---
const approvalValidationSchema = Yup.object().shape({
    network: Yup.string().required("This field is required"),
    subnet: Yup.string().required("This field is required"),
});

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null; isLink?: boolean, linkPrefix?: string }> = ({ label, value, isLink = false, linkPrefix = '' }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            {isLink && typeof displayValue === 'string' && displayValue !== '-' ? (
                <Link href={linkPrefix + displayValue} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline' }}>
                    <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>
                        {displayValue}
                    </Typography>
                </Link>
            ) : (
                <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>
                    {typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}
                </Typography>
            )}
        </Box>
    );
};


const AIAgentApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [aiAgent, setAIAgent] = useState<AIAgentDetailsData | null>(null);
    const [agentEndpoint, setAgentEndpoint] = useState<string | null>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const fallback = '-';
    const theme = useTheme();

    React.useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                const data = await queryGraphql(
                    `
                        query GetSolutionAIAgent($getSolutionAiAgentId: String!) {
                          getSolutionAIAgent(id: $getSolutionAiAgentId) {
                            id name solutionType cloudProvider environment status reasonForRejection
                            details { aiAgent { projectId region imageUrl gpu modelName modelTemperature modelMaxTokens modelStreaming repoUrl network subnet } }
                            solutionOwner { id name googleProfilePicture }
                            application { id applicationName }
                            solutionApprover { name email googleProfilePicture id }
                          }
                        }
                    `,
                    { getSolutionAiAgentId: id }
                );
                if (data.getSolutionAIAgent) {
                    setAIAgent(data.getSolutionAIAgent);
                } else {
                    console.log("AI Agent details not found.");
                    setAIAgent(null);
                }
            } catch (error) {
                console.log("Error fetching AI Agent details:", error);
                setAIAgent(null);
            }
        };
        fetchData();
    }, [id]);

    React.useEffect(() => {
        const fetchEndpointUrl = async () => {
            if (!aiAgent?.id || aiAgent.status !== 'active') return;
            try {
                const data = await queryGraphql( ` query Query($resourceStatusId: String!) { resourceStatus(id: $resourceStatusId) { id solutionEndpointUrl } }`, { resourceStatusId: aiAgent.id } );
                if (data.resourceStatus?.solutionEndpointUrl) {
                    setAgentEndpoint(data.resourceStatus.solutionEndpointUrl);
                }
            } catch (error) {
                console.log("Error fetching agent endpoint URL:", error);
            }
        };
        fetchEndpointUrl();
    }, [aiAgent]);

    const statusDisplay = aiAgent?.status ? capitalize(aiAgent.status) : "Status Unknown";
    const statusBgColor = aiAgent?.status === "active" || aiAgent?.status === "approved" ? "#12B76A10" : aiAgent?.status === "rejected" ? "#F0443810" : "#FDB02210";
    const statusIcon = aiAgent?.status === "active" || aiAgent?.status === "approved" ? "/assets/tickcircle.svg" : aiAgent?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";
    const projectIdLabel = aiAgent?.cloudProvider === 'aws' ? 'Account ID' : aiAgent?.cloudProvider === 'azure' ? 'Subscription ID' : 'Project ID';

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/aisolutions/requests/aiagent" onClick={(e) => { e.preventDefault(); navigate("/aisolutions/requests/aiagent") }}> AI Agents Requests </Link>
                    <Typography color="text.primary">Agent Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/requests/aiagent")} >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">Agent Details</Typography>
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11} md={11} lg={11}> 
                    {aiAgent && (
                        <Formik
                            initialValues={{
                                network: aiAgent.details.aiAgent.network || "",
                                subnet: aiAgent.details.aiAgent.subnet || "",
                            }}
                            validationSchema={aiAgent.cloudProvider !== 'gcp' ? approvalValidationSchema : Yup.object()}
                            enableReinitialize
                            onSubmit={async (values) => {
                                if (!id) return;
                                
                                let detailsPayload = null; // Default to null for GCP
                                if (aiAgent.cloudProvider === 'aws' || aiAgent.cloudProvider === 'azure') {
                                    detailsPayload = { aiAgent: { network: values.network, subnet: values.subnet } };
                                }

                                try {
                                    const approvalData = await queryGraphql(
                                        `mutation ApproveSolutionAIAgent($input: ApproveSolutionInput!) { approveSolutionAIAgent(input: $input) { id } }`,
                                        { input: { id, details: detailsPayload } }
                                    );
                                    if (approvalData.approveSolutionAIAgent?.id) {
                                        setOpenApproveModal(false);
                                        navigate("/aisolutions/requests/aiagent");
                                    } else { console.error("Approval failed", approvalData); }
                                } catch (error) { console.error("Error during approval:", error); }
                            }}
                        >
                            {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                                <Form id="approve-agent-main-form">
                                    <Box my={2}>
                                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                                                <Box sx={{mb: {xs: 1, sm: 0}}}><Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{aiAgent.name || fallback}</Typography><Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${aiAgent.solutionOwner?.name || fallback}`}</Typography></Box>
                                                <Box textAlign="right"><Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end' }}><img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{textTransform: 'capitalize'}}>{statusDisplay}</Typography></Box></Box>
                                            </Box>
                                        </Box>
                                        
                                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label={projectIdLabel} value={aiAgent.details.aiAgent.projectId} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={aiAgent.cloudProvider?.toUpperCase()} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Region" value={capitalize(aiAgent.details.aiAgent.region || "")} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Environment" value={capitalize(aiAgent.environment || "")} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Application Name" value={aiAgent.application?.applicationName} /></Grid>
                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="GPU" value={aiAgent.details.aiAgent.gpu || "0"} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Image URL" value={aiAgent.details.aiAgent.imageUrl} /></Grid>
                                                {agentEndpoint && (<Grid item xs={12} sm={6} md={3}><Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Agent Endpoint</Typography><Tooltip title={<Link href={agentEndpoint} target="_blank" rel="noopener noreferrer" sx={{color: '#FFFFFF'}}>{agentEndpoint}</Link>} ><Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, cursor: 'pointer' }} /></Tooltip></Grid>)}
                                            </Grid>
                                            <Divider sx={{ my: 2.5 }} />
                                            <Typography variant="subtitle1" fontFamily="Gilroy-SemiBold" sx={{ mb: 2 }}>Model Details</Typography>
                                            <Grid container spacing={2.5} sx={{mb:2}}>
                                               
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Model Name" value={aiAgent.details.aiAgent.modelName} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Model Temperature" value={aiAgent.details.aiAgent.modelTemperature} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Model Tokens" value={aiAgent.details.aiAgent.modelMaxTokens} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Model Streaming" value={aiAgent.details.aiAgent.modelStreaming} /></Grid>
                                                
                                            </Grid>
                                             {/* {(aiAgent.status === 'active' || aiAgent.status === 'rejected') && (
                                                 <Box>
                                                    <Divider sx={{ my: 2.5 }} />

                                                    <Grid container spacing={2.5}>
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">Actioned by</Typography>
                                                            <Box display="flex" alignItems="center" mt={0.5}>
                                                                <Avatar sx={{ height: "24px", width: "24px", mr: 1 }} src={aiAgent.solutionApprover?.googleProfilePicture} />
                                                                <Typography fontFamily="Gilroy-Semibold" fontSize="14px" noWrap>{aiAgent.solutionApprover?.name || fallback}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        {aiAgent.status === 'rejected' && <Grid item xs={12} sm={6} md={9}><DetailItem label="Reason for Rejection" value={aiAgent.reasonForRejection} /></Grid>}
                                                    </Grid>
                                                 </Box>
                                            )} */}

                                           
                                           
                                           
                                        </Box>
                                         {aiAgent.status === 'rejected' && (
                                                                                         <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                                                            <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                                                                                            <Divider sx={{ marginY: 2 }} />
                                                                                            <DetailItem label="Reason for Rejection" value={aiAgent.reasonForRejection} />
                                                                                         </Box>
                                                                                    )}

                                            <Box sx={{ mt: 0, p: 0, borderTop: "1px solid #e0e0e0" }}>
                                                            
                                                                            {aiAgent.status === 'active' && aiAgent.cloudProvider != 'gcp' && (
                                                                                                         <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                                                                            <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                                                                                                            <Divider sx={{ marginY: 2 }} />
                                                                                                            <Grid container spacing={2.5}>
                                                                                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Network" value={aiAgent.details.aiAgent.network} /></Grid>
                                                                                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Subnets" value={aiAgent.details.aiAgent.subnet} /></Grid>
                                            
                                                                                                            </Grid>
                                                                                                         </Box>
                                                                                                    )}
                                                                                                    </Box>

                                        
                                        {aiAgent.status === 'pending' && (aiAgent.cloudProvider === 'aws' || aiAgent.cloudProvider === 'azure') && (
                                            <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <Grid container spacing={2.5}>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Network" /><TextField name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Network" variant="filled" fullWidth hiddenLabel error={touched.network && Boolean(errors.network)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="network" /></div></Grid>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Subnet" /><TextField name="subnet" value={values.subnet} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Subnet" variant="filled" fullWidth hiddenLabel error={touched.subnet && Boolean(errors.subnet)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="subnet" /></div></Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                    </Box>
                                    
                                    {aiAgent?.status === "pending" && user.roles.some((role: string) => ["infrastructure_admin"].includes(role)) && (
                                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={3}>
                                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold" }} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
                                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "rgba(0,0,0,0.85)"}, textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&.Mui-disabled': { backgroundColor: theme.palette.action.disabledBackground || 'rgba(0, 0, 0, 0.12)', color: theme.palette.action.disabled || 'rgba(0, 0, 0, 0.26)' } }} variant="contained" size="large" disabled={aiAgent.cloudProvider !== 'gcp' && (!isValid || !dirty)} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                                        </Box>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
            </Grid>

            <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Request</Typography><IconButton aria-label="close" onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton></Box>
                    <Divider />
                    <DialogContent><Typography>Are you sure you want to approve this AI Agent request?</Typography></DialogContent>
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                        <Button type="submit" form="approve-agent-main-form" sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px", display: 'flex', flexDirection: 'column' }}>
                    <Formik
                        initialValues={{ reasonForRejection: "" }}
                        validationSchema={rejectValidationSchema}
                        onSubmit={async ({ reasonForRejection }) => {
                            if (!id) return;
                            try {
                                const rejectionData = await queryGraphql(`mutation RejectSolutionAIAgent($input: RejectSolutionInput!) { rejectSolutionAIAgent(input: $input) { id name } }`,{ input: { id: id, reasonForRejection } });
                                if (rejectionData.rejectSolutionAIAgent?.id) {
                                    setOpenRejectModal(false);
                                    navigate("/aisolutions/requests/aiagent");
                                }
                            } catch (error) { console.error("Error during rejection:", error); }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                            <Form id="reject-agent-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject AI Agent Request</Typography><IconButton aria-label="close" onClick={() => setOpenRejectModal(false)}><Close fontSize="small" /></IconButton></Box>
                                <Divider />
                                <Box sx={{ p: 3, flexGrow: 1 }}>
                                    <CompulsoryFieldsLabel fieldName="Comments" />
                                    <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                    <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                    <Button variant="outlined" onClick={() => setOpenRejectModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                    <Button type="submit" form="reject-agent-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained">Reject</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};


const AIAgentApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
    };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-aiagent"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <AIAgentApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default AIAgentApproveFormPage;

// import {
//     Avatar,
//     Box,
//     Breadcrumbs,
//     Button,
//     // Chip, // Not used
//     Container,
//     Divider,
//     Grid,
//     IconButton,
//     Link,
//     Modal,
//     TextField,
//     Tooltip,
//     Typography,
//     DialogContent,
//     useTheme,
//     capitalize,
// } from "@mui/material";
// import React, { useState } from "react";
// import * as Yup from "yup";

// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { useNavigate, useParams } from "react-router-dom";
// import queryGraphql from "../../helpers/ApiInterface";
// import moment from "moment";
// import { useAuth } from "../../helpers/AuthProvider";
// import { Close } from "@mui/icons-material";
// import { Formik, ErrorMessage, Form } from "formik";

// import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
// import MiniBot from "../MiniBot";
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";

// // Helper function to render detail items safely
// const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null; isLink?: boolean, linkPrefix?: string }> = ({ label, value, isLink = false, linkPrefix = '' }) => {
//     const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
//     return (
//         <Box>
//             <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">
//                 {label}
//             </Typography>
//             {isLink && typeof displayValue === 'string' && displayValue !== '-' ? (
//                 <Link href={`${linkPrefix}${displayValue}`} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline' }}>
//                     <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>
//                         {displayValue}
//                     </Typography>
//                 </Link>
//             ) : (
//                 <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>
//                     {typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}
//                 </Typography>
//             )}
//         </Box>
//     );
// };


// const AIAgentApproveFormPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { id } = useParams<{ id: string }>();
//     const { user } = useAuth();
//     const [aiAgent, setAIAgent] = useState<any>({});
//     // const [config, setConfig] = useState<any>({}); // config fetched but not used in JSX
//     const [openRejectModal, setOpenRejectModal] = useState(false);
//     const [openApproveModal, setOpenApproveModal] = useState(false);
//     const fallback = '-';
//     const theme = useTheme();

//     React.useEffect(() => {
//         const fetchData = async () => {
//             if (!id) return;
//             try {
//                 const data = await queryGraphql(
//                     `
//                         query GetAIAgent($getAiAgentId: String!) {
//                           getAIAgent(id: $getAiAgentId) {
//                             id
//                             agentName
//                             aIAgentOwner { id name email googleProfilePicture }
//                             aIAgentApprover { id name email googleProfilePicture }
//                             status
//                             environment
//                             projectId
//                             serviceName
//                             region
//                             imageUrl
//                             gpu
//                             applicationName
//                             modelName
//                             modelTemperature
//                             modelMaxTokens
//                             modelStreaming
//                             updatedAt
//                             createdAt
//                             repoUrl
//                             reasonForRejection
//                           }
//                         }
//                     `,
//                     { getAiAgentId: id }
//                 );
//                 if (data.getAIAgent) {
//                     setAIAgent(data.getAIAgent);
//                 }
//             } catch (error) {
//                 console.log("Error fetching AI Agent details:", error);
//             }
//         };
//         fetchData();
//     }, [id]);

//     const dateFormatter = (dateValue: string | number | null) => {
//         if (!dateValue) return fallback;
//         const numericDate = Number(dateValue);
//         if (isNaN(numericDate)) return fallback;
//         return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY");
//     };
    
//     const dateTimeFormatter = (dateValue: string | number | null) => {
//         if (!dateValue) return fallback;
//         const numericDate = Number(dateValue);
//         if (isNaN(numericDate)) return fallback;
//         return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
//     };


//     return (
//         <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
//             <MiniBot />
//             <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/aisolutions/requests/aiagent">
//                         AI Agents
//                     </Link>
//                     <Typography color="text.primary">Agent Details</Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={1}>
//                     <IconButton
//                         edge="start"
//                         aria-label="back"
//                         color="inherit"
//                         onClick={() => navigate("/aisolutions/requests/aiagent")} 
//                     >
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                         Agent Details
//                     </Typography>
//                 </Box>
//             </Box>
//             <Grid container justifyContent="center">
//                 <Grid item xs={11} md={11} lg={11}> 
//                     {aiAgent.id && (
//                         <Box my={2}>
//                             {/* Agent Name and Status Bar */}
//                             <Box
//                                 bgcolor={
//                                     aiAgent.status === "active" || aiAgent.status === "approved"
//                                         ? "#12B76A10"
//                                         : aiAgent.status === "rejected"
//                                             ? "#F0443810"
//                                             : "#FDB02210"
//                                 }
//                                 borderRadius="12px 12px 0px 0px"
//                                 p={2}
//                                 boxShadow={1}
//                             >
//                                 <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
//                                     <Box sx={{mb: {xs: 1, sm: 0}}}>
//                                         <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>
//                                             {aiAgent.agentName || fallback} 
//                                         </Typography>
//                                         {aiAgent.status == "pending" &&
//                                                                                 user.roles?.some((e: string) => ["application_owner"].includes(e)) && (
//                                                                                     <IconButton
//                                                                                         edge="start"
//                                                                                         aria-label="back"
//                                                                                         color="inherit"
//                                                                                         onClick={() => navigate(`/applications/edit/${aiAgent.id}`)}
//                                                                                     >
//                                                                                         <img width="20px" height="20px" src="/assets/edit-icon.svg" />
//                                                                                     </IconButton>
//                                                                                 )}
//                                         <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">
//                                             {`Resource Owner: ${aiAgent.aIAgentOwner?.name || fallback} | Added On: ${dateFormatter(aiAgent.createdAt)}`}
//                                         </Typography>
//                                     </Box>
//                                     <Box textAlign="right">
//                                         <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end' }}>
//                                             <img
//                                                 src={
//                                                     aiAgent.status === "active" || aiAgent.status === "approved"
//                                                         ? "/assets/tickcircle.svg"
//                                                         : aiAgent.status === "rejected"
//                                                             ? "/assets/reject.svg"
//                                                             : "/assets/clock.svg"
//                                                 }
//                                                 alt="Status Icon"
//                                                 style={{ width: "20px", marginRight: "8px" }}
//                                             />
//                                             <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{textTransform: 'capitalize'}}>
//                                                 {aiAgent.status === "active" || aiAgent.status === "approved"
//                                                     ? "Active"
//                                                     : aiAgent.status === "rejected"
//                                                         ? "Rejected"
//                                                         : aiAgent.status ? capitalize(aiAgent.status) : "Pending Approval"}
//                                             </Typography>
//                                         </Box>
//                                         {(aiAgent.status === "active" || aiAgent.status === "approved" || aiAgent.status === "rejected") && (
//                                             <Typography fontSize="12px" fontFamily="Gilroy-Regular" color="text.secondary">{`On: ${dateFormatter(aiAgent.updatedAt)}`}</Typography>
//                                         )}
//                                         {aiAgent.aIAgentApprover?.name && (aiAgent.status === "active" || aiAgent.status === "approved" || aiAgent.status === "rejected") && (
//                                             <Typography fontSize="12px" fontFamily="Gilroy-Regular" color="text.secondary" sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5}}>
//                                                 By:
//                                                 <Avatar 
//                                                     sx={{ width: 16, height: 16, ml: 0.5, mr: 0.5 }} 
//                                                     src={aiAgent.aIAgentApprover?.googleProfilePicture}
//                                                     alt={aiAgent.aIAgentApprover?.name?.charAt(0).toUpperCase()}
//                                                 /> 
//                                                 {aiAgent.aIAgentApprover?.name}
//                                             </Typography>
//                                         )}
//                                     </Box>
//                                 </Box>
//                             </Box>

//                             {/* Details Box */}
//                             <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
//                                 {/* Section 1: General Agent Info (as per image) */}
//                                 <Grid container spacing={2.5}>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Project ID" value={aiAgent.projectId} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Region" value={aiAgent.region ? capitalize(aiAgent.region) : fallback} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Service Name" value={aiAgent.serviceName} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Image URL" value={aiAgent.imageUrl} /></Grid>

//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="GPU" value={aiAgent.gpu || "0"} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Environment" value={aiAgent.environment ? capitalize(aiAgent.environment) : fallback} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Application Name" value={aiAgent.applicationName} /></Grid>
//                                     {/* Conditionally render Repo URL */}
//                                     {aiAgent.repoUrl && aiAgent.status == "active"  && (
//                                                             <Grid item xs={12} sm={5} md={3}>
//                                                                 <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Repo URL</Typography>
//                                                                 <Tooltip
//                                                                     sx={{ whiteSpace: "pre-line" }}
//                                                                     title={
//                                                                         <>
//                                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" fontWeight={700} color="common.white"> {/* Ensure white for tooltip */}
//                                                                                 Repo URL
//                                                                             </Typography>
//                                                                             <br />
//                                                                             <Link
//                                                                                 href={aiAgent.repoUrl} // repoUrl is checked by isRepoUrlVisible
//                                                                                 target="_blank"
//                                                                                 rel="noopener noreferrer"
//                                                                                 sx={{
//                                                                                     display: 'block',
//                                                                                     wordBreak: 'break-all',
//                                                                                     color: '#ffffff', 
//                                                                                     textDecoration: 'none',
//                                                                                     fontFamily: "Gilroy-Regular",
//                                                                                     fontSize: "14px",
//                                                                                     '&:hover': {
//                                                                                         textDecoration: 'underline',
//                                                                                     },
//                                                                                 }}
//                                                                             >
//                                                                                 {aiAgent.repoUrl}
//                                                                             </Link>
//                                                                             <br/>
//                                                                         </>
//                                                                     }
//                                                                     slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
//                                                                 >
//                                                                     <Avatar
//                                                                         src="/assets/info.svg"
//                                                                         alt="info"
//                                                                         sx={{ height: 16, width: 16, /* marginLeft: "4px", // Potentially remove if label is above */ cursor: 'pointer' }}
//                                                                     />
//                                                                 </Tooltip>
//                                                             </Grid>
//                                                         )}
//                                 </Grid>

//                                 <Divider sx={{ my: 2.5 }} />

//                                 <Typography variant="subtitle1" fontFamily="Gilroy-SemiBold" sx={{ mb: 2 }}>
//                                     Model Details
//                                 </Typography>
//                                 <Grid container spacing={2.5} sx={{mb:2}}>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Model Name" value={aiAgent.modelName} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Model Temperature" value={aiAgent.modelTemperature} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Model Tokens" value={aiAgent.modelMaxTokens} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="Model Streaming" value={aiAgent.modelStreaming} /></Grid>
//                                 </Grid>

//                                 {aiAgent.status && aiAgent.status.toLowerCase() === 'rejected' && aiAgent.reasonForRejection && (
//                                         <Box sx={{ mt:0, borderTop: "1px solid #e0e0e0" }}>
//                                             <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color: theme.palette.error.main, pb:"0.5vh" , mt:2}}>
//                                                 Reason for Rejection
//                                             </Typography>
//                                             <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#333333'}}>
//                                                 {aiAgent.reasonForRejection}
//                                             </Typography>
//                                         </Box>
//                                     )}
//                             </Box>
//                         </Box>
//                     )}
                    
//                     {aiAgent.status === "pending" &&
//                         user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
//                             <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={3}>
//                                 <Button
//                                     sx={{
//                                         color: "black",
//                                         borderColor: "black",
//                                         fontWeight: "bold",
//                                         textTransform: "none",
//                                         px: 4,
//                                         fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px",
//                                     }}
//                                     variant="outlined"
//                                     size="large"
//                                     onClick={() => setOpenRejectModal(true)}
//                                 >
//                                     Reject Request
//                                 </Button>
//                                 <Button
//                                     sx={{
//                                         backgroundColor: "black",
//                                         color: "white",
//                                         fontWeight: "bold",
//                                         "&:hover": {
//                                             backgroundColor: "rgba(0,0,0,0.85)",
//                                         },
//                                         textTransform: "none",
//                                         px: 4,
//                                         fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px",
//                                     }}
//                                     variant="contained"
//                                     size="large"
//                                     onClick={() => setOpenApproveModal(true)}
//                                 >
//                                     Approve Request
//                                 </Button>
//                             </Box>
//                         )}
//                     <Modal
//                         open={openApproveModal}
//                         onClose={() => setOpenApproveModal(false)}
//                     >
//                         <Box
//                             sx={{
//                                 position: "absolute",
//                                 top: "50%",
//                                 left: "50%",
//                                 transform: "translate(-50%, -50%)",
//                                 width: { xs: '90%', sm: '80%', md: 700 },
//                                 bgcolor: "white",
//                                 border: "0px",
//                                 boxShadow: 24,
//                                 borderRadius: "16px",
//                                 maxHeight: '90vh',
//                                 display: 'flex',
//                                 flexDirection: 'column'
//                             }}
//                         >
//                             <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
//                                 <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">
//                                     Approve AI Agent Request
//                                 </Typography>
//                                 <IconButton
//                                     aria-label="close"
//                                     onClick={() => setOpenApproveModal(false)}
//                                 >
//                                     <Close fontSize="small" />
//                                 </IconButton>
//                             </Box>
//                             <Divider />
//                             <Box sx={{p:2, overflowY: 'auto', flexGrow: 1}}>
//                                 <Typography fontSize="16px" gutterBottom>
//                                     Are you sure you want to approve this AI Agent request?
//                                 </Typography>
//                                 <Typography fontSize="14px" color="text.secondary" gutterBottom>
//                                     On approving, the following AI Agent configuration will be set to active:
//                                 </Typography>
//                                 <DialogContent sx={{ p: 2, backgroundColor:'#FAFAFA', borderRadius:'8px', mt: 1}}>
//                                     <Grid container spacing={1.5}>
//                                         <Grid item xs={12} sm={6}><DetailItem label="Agent Name" value={aiAgent.agentName} /></Grid>
//                                         <Grid item xs={12} sm={6}><DetailItem label="Environment" value={aiAgent.environment ? aiAgent.environment : fallback} /></Grid>
//                                         <Grid item xs={12} sm={6}><DetailItem label="Project ID" value={aiAgent.projectId} /></Grid>
//                                         <Grid item xs={12} sm={6}><DetailItem label="Service Name" value={aiAgent.serviceName} /></Grid>
//                                         <Grid item xs={12} sm={6}><DetailItem label="Model Name" value={aiAgent.modelName} /></Grid>
//                                         <Grid item xs={12} sm={6}><DetailItem label="Region" value={aiAgent.region ? aiAgent.region : fallback} /></Grid>
//                                     </Grid>
//                                 </DialogContent>
//                             </Box>
//                             <Divider />
//                             <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
//                                 <Button
//                                     variant="outlined"
//                                     onClick={() => setOpenApproveModal(false)}
//                                     sx={{ color: 'black', borderColor: 'black', textTransform: 'none', fontFamily: "Gilroy-Bold" }}
//                                 >
//                                     Cancel
//                                 </Button>
//                                 <Button
//                                     sx={{
//                                         backgroundColor: "black",
//                                         color: "white",
//                                         fontWeight: "bold",
//                                         "&:hover": {
//                                             backgroundColor: "rgba(0,0,0,0.8)",
//                                         },
//                                         textTransform: "none",
//                                         px: 3,
//                                         fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px",
//                                     }}
//                                     variant="contained"
//                                     onClick={async () => {
//                                         if (!aiAgent.id || !id) return;
//                                         try {
//                                             const approvalData = await queryGraphql(
//                                                 `
//                                                 mutation ApproveAIAgent($approveAiAgentId: String!) {
//                                                   approveAIAgent(id: $approveAiAgentId) {
//                                                     id
//                                                     status
//                                                   }
//                                                 }
//                                                 `,
//                                                 { approveAiAgentId: aiAgent.id }
//                                             );

//                                             if (approvalData.approveAIAgent?.id) {
//                                                 setOpenApproveModal(false);
//                                                 navigate("/aisolutions/requests/aiagent");
//                                             } else {
//                                                 console.error("Approval failed", approvalData);
//                                             }
//                                         } catch(error) {
//                                             console.error("Error during AI Agent approval:", error);
//                                         }
//                                     }}
//                                 >
//                                     Approve
//                                 </Button>
//                             </Box>
//                         </Box>
//                     </Modal>
//                     <Modal
//                         open={openRejectModal}
//                         onClose={() => setOpenRejectModal(false)}
//                     >
//                         <Box
//                             sx={{
//                                 position: "absolute",
//                                 top: "50%",
//                                 left: "50%",
//                                 transform: "translate(-50%, -50%)",
//                                 width: { xs: '90%', sm: '80%', md: 700 },
//                                 bgcolor: "white",
//                                 border: "0px",
//                                 boxShadow: 24,
//                                 borderRadius: "16px",
//                                 maxHeight: '90vh',
//                                 display: 'flex',
//                                 flexDirection: 'column'
//                             }}
//                         >
//                             <Formik
//                                 onSubmit={async ({ reasonForRejection }) => {
//                                     if (!aiAgent.id || !id) return;
//                                     try {
//                                         const rejectionData = await queryGraphql(
//                                             `
//                                             mutation RejectAIAgent($input: ApplicationRejectInput!) {
//                                               rejectAIAgent(input: $input) {
//                                                 id
//                                                 status
//                                                 reasonForRejection
//                                               }
//                                             }
//                                             `,
//                                             {
//                                                 input: { 
//                                                     id: aiAgent.id,
//                                                     reasonForRejection,
//                                                 },
//                                             }
//                                         );
                                        
//                                         if (rejectionData.rejectAIAgent?.id) {
//                                             setOpenRejectModal(false);
//                                             navigate("/aisolutions/requests/aiagent");
//                                         } else {
//                                             console.error("Rejection failed", rejectionData);
//                                         }
//                                     } catch (error) {
//                                         console.error("Error during AI Agent rejection:", error);
//                                     }
//                                 }}
//                                 validationSchema={Yup.object().shape({
//                                     reasonForRejection: Yup.string()
//                                         .required("Reason for rejection is required.")
//                                         .min(5, "Reason must be at least 5 characters long.")
//                                         .matches(/^[a-zA-Z0-9.,!?'"\s-]*$/, "Reason contains invalid characters.")
//                                         .strict(),
//                                 })}
//                                 initialValues={{ reasonForRejection: "" }}
//                             >
//                                 {({
//                                     handleChange,
//                                     handleBlur,
//                                     values,
//                                     errors,
//                                     touched,
//                                     isValid,
//                                     dirty,
//                                 }) => {
//                                     return (
//                                         <Form id="reject-ai-agent-form" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
//                                             <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
//                                                 <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">
//                                                     Reject AI Agent Request
//                                                 </Typography>
//                                                 <IconButton
//                                                     aria-label="close"
//                                                     onClick={() => setOpenRejectModal(false)}
//                                                 >
//                                                     <Close fontSize="small" />
//                                                 </IconButton>
//                                             </Box>
//                                             <Divider />
//                                             <Box sx={{p:2, overflowY: 'auto', flexGrow: 1}}>
//                                                 <Typography fontSize="16px" gutterBottom>
//                                                     Please provide comments for the requestor explaining the reason for rejection.
//                                                 </Typography>
                                            
//                                                 <CompulsoryFieldsLabel fieldName="Comments" />
//                                                 <TextField
//                                                     sx={{
//                                                         "& .MuiFilledInput-root": {
//                                                             border:
//                                                                 errors.reasonForRejection && touched.reasonForRejection
//                                                                     ? `1px solid ${theme.palette.error.main}`
//                                                                     : "1px solid transparent",

//                                                             "&.Mui-focused": {
//                                                                 backgroundColor: "white",
//                                                                 border: `1px solid ${errors.reasonForRejection && touched.reasonForRejection ? theme.palette.error.main : theme.palette.primary.main}`,
//                                                             },
//                                                             backgroundColor: touched.reasonForRejection && errors.reasonForRejection ? '#FFF0F0' : '#F8F8F8',
//                                                         },
//                                                     }}
//                                                     variant="filled"
//                                                     fullWidth
//                                                     multiline
//                                                     rows={4}
//                                                     name="reasonForRejection"
//                                                     value={values.reasonForRejection}
//                                                     onChange={handleChange}
//                                                     onBlur={handleBlur}
//                                                     placeholder="Enter comments for rejection..."
//                                                     hiddenLabel
//                                                     InputProps={{
//                                                         disableUnderline: true,
//                                                         style: {
//                                                             borderRadius: "8px",
//                                                             fontSize: "14px",
//                                                             fontFamily: "Gilroy-Regular"
//                                                         },
//                                                     }}
//                                                     error={touched.reasonForRejection && Boolean(errors.reasonForRejection)}
//                                                 />
//                                                 <ErrorMessage name="reasonForRejection">
//                                                     {msg => <Typography color="error.main" variant="caption" sx={{ mt: 0.5, ml: 0.5, fontFamily: "Gilroy-Regular" }}>{msg}</Typography>}
//                                                 </ErrorMessage>
//                                             </Box>
//                                             <Divider />
//                                             <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
//                                                 <Button
//                                                     variant="outlined"
//                                                     onClick={() => setOpenRejectModal(false)}
//                                                     sx={{ color: 'black', borderColor: 'black', textTransform: 'none', fontFamily: "Gilroy-Bold" }}
//                                                 >
//                                                     Cancel
//                                                 </Button>
//                                                 <Button
//                                                     sx={{
//                                                         backgroundColor: "black",
//                                                         color: "white",
//                                                         fontWeight: "bold",
//                                                         "&:hover": {
//                                                             backgroundColor: "rgba(0,0,0,0.8)",
//                                                         },
//                                                         textTransform: "none",
//                                                         px: 3,
//                                                         fontFamily: "Gilroy-Bold",
//                                                         fontSize: "14px",
//                                                         '&.Mui-disabled': { 
//                                                             backgroundColor: theme.palette.action.disabledBackground,
//                                                             color: theme.palette.action.disabled,
//                                                         }
//                                                     }}
//                                                     variant="contained"
//                                                     type="submit"
//                                                     form="reject-ai-agent-form"
//                                                     disabled={!(isValid && dirty)}
//                                                 >
//                                                     Reject
//                                                 </Button>
//                                             </Box>
//                                         </Form>
//                                     );
//                                 }}
//                             </Formik>
//                         </Box>
//                     </Modal>
//                 </Grid>
//             </Grid>
//         </Container>
//     );
// };


// const AIAgentApproveFormPage: React.FC = () => {
//     const theme = useTheme();

//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//     };

//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer
//                 index={"requests-ai-agent"}
//                 onStateChange={handleDrawerStateChange}
//             />
//             <Box
//                 component="main"
//                 sx={{
//                     flexGrow: 1,
//                     bgcolor: '#fafafa',
//                     p: 0, 
//                     transition: theme.transitions.create('margin', {
//                         easing: theme.transitions.easing.sharp,
//                         duration: theme.transitions.duration.enteringScreen,
//                     }),
//                     marginLeft: { xs: 0, sm: `${currentMarginLeft}px` },
//                 }}
//             >
//                 <AIAgentApproveFormPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default AIAgentApproveFormPage;